import React, { useState, useEffect, forwardRef, useRef } from 'react';
import { Box, Button, IconButton, Modal, Popover, Typography, TextField, Select, MenuItem } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { filterRoomsByLessonType } from '../../../../constants/roomTypes';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import { FormInputDropdown } from '../../../form/FormInputDropdown';
import { FormInputDate } from '../../../../form-components/FormInputDate';
import { getPointTrainers } from '../../../../api/staff';
import { searchBatchReservationLessons, getPrivateBatchPreview } from '../../../../api/lesson';
import { errorMessages, validateDateRange } from '../../../../utils/methods';
import CloseIcon from '@mui/icons-material/Close';
import CalendarIcon from '../../../CalendarIcon';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { getAllBranchOption, getHeadBranchId, getAllPointRooms } from '../../../../api/point';

// 요일 순서/라벨 — 일~토. dayOfWeek는 영문 enum으로 백엔드 전송.
const DAY_LABELS = {
  SUNDAY: '일',
  MONDAY: '월',
  TUESDAY: '화',
  WEDNESDAY: '수',
  THURSDAY: '목',
  FRIDAY: '금',
  SATURDAY: '토',
};
const DAY_KEYS = Object.keys(DAY_LABELS);

// 시간 옵션 생성 (00 ~ 23)
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const hour = i.toString().padStart(2, '0');
  return { value: hour, label: `${hour}시` };
});
// 분 옵션 생성 (10분 단위)
const MINUTE_OPTIONS = [
  { value: '00', label: '00분' },
  { value: '10', label: '10분' },
  { value: '20', label: '20분' },
  { value: '30', label: '30분' },
  { value: '40', label: '40분' },
  { value: '50', label: '50분' },
];

const timeSelectSx = {
  width: '80px',
  height: '41px',
  borderRadius: '4px',
  border: '1px solid #b3b3b3',
  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '& .MuiSelect-select': { fontSize: '14px' },
};

// 시:분 드롭다운 한 쌍. value는 "HH:mm" 문자열.
const TimePicker = ({ value, onChange, fallback = '09:00' }) => {
  const [hour, minute] = (value || fallback).split(':');
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <Select value={hour} onChange={e => onChange(`${e.target.value}:${minute}`)} size="small" sx={timeSelectSx}>
        {HOUR_OPTIONS.map(opt => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
      <Select value={minute} onChange={e => onChange(`${hour}:${e.target.value}`)} size="small" sx={timeSelectSx}>
        {MINUTE_OPTIONS.map(opt => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

const dateFieldRender = t => params => {
  const { placeholder, ...restParams } = params;
  return (
    <TextField
      {...restParams}
      inputProps={{ ...params.inputProps, placeholder: t('날짜입력') }}
      sx={{
        '& .MuiSvgIcon-root': { fill: '#0F7F85 !important', width: '18px', height: '20px' },
        width: '160px',
        '& .MuiInputBase-root': {
          borderRadius: '4px',
          height: '44px',
          border: '1px solid #b3b3b3',
        },
        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
        '& input': { fontSize: '14px', fontWeight: '600', color: '#b3b3b3' },
      }}
      size="small"
    />
  );
};

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '626px',
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: '16px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
};

const labelSx = { fontSize: '18px', fontWeight: '600', minWidth: '98px', color: '#262626', textAlign: 'center' };

// 시간 일괄 적용 팝업 박스 스타일
const bulkModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '360px',
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: '16px',
  p: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
};

// ---------------------------------------------------------------------------
// 개인레슨 분기 — 요일/시간으로 예약기간 동안 수업을 미리 생성하는 새 흐름.
// 한 요일에 시간대를 여러 개 둘 수 있다(예: 월 10:00~11:00, 12:00~13:00). 백엔드는 classTimeInfoList 를
// 전부 순회하므로 같은 요일이 여러 번 와도 된다. 단 요청 안 시간대끼리의 겹침은 preview 가 표시하지 않아 폼에서 막는다.
// ---------------------------------------------------------------------------

// 화면은 월요일부터 보여준다(전송값은 요일 enum 이라 순서 무관).
const PRIVATE_DAY_ORDER = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
const DEFAULT_SLOT = { startTime: '09:00', endTime: '10:00' };
const LAST_SLOT_START = '23:00';

const toMinutes = time => {
  const [hour, minute] = time.split(':').map(Number);
  return hour * 60 + minute;
};
const slotsOverlap = (a, b) =>
  toMinutes(a.startTime) < toMinutes(b.endTime) && toMinutes(b.startTime) < toMinutes(a.endTime);
const sortSlots = slots => [...slots].sort((a, b) => toMinutes(a.startTime) - toMinutes(b.startTime));

// 같은 요일에 새 시간대를 추가할 때 마지막 시간대 바로 뒤를 같은 길이로 제안한다.
const suggestNextSlot = (slots, fallback) => {
  if (slots.length === 0) return { ...fallback };
  const last = slots[slots.length - 1];
  const length = toMinutes(last.endTime) - toMinutes(last.startTime);
  const start = moment(last.endTime, 'HH:mm');
  const end = start.clone().add(length, 'minutes');
  if (start.format('HH:mm') > LAST_SLOT_START || !end.isSame(start, 'day')) return { ...fallback };
  return { startTime: start.format('HH:mm'), endTime: end.format('HH:mm') };
};

const sectionTitleSx = { fontSize: '14px', fontWeight: '700', color: '#262626', mb: '12px' };
const sectionSx = { borderTop: '1px solid #ededed', pt: '20px', mt: '20px' };
const fieldRowSx = { display: 'flex', flexDirection: 'row', alignItems: 'center', height: '41px', mb: '12px' };
const fieldLabelSx = { fontSize: '14px', fontWeight: '600', color: '#555555', width: '88px', flexShrink: 0 };

const PrivateLessonBatchForm = forwardRef(({ modal, setModal, bigUser, ticket, user, onClose }, ref) => {
  const { t } = useTranslation();
  const toast = useToast();
  const userApp = useSelector(state => state.user.user);
  const [trainers, setTrainers] = useState([]);
  const [rooms, setRooms] = useState([]);

  // 요일별 시간대 목록. 시간대가 하나 이상인 요일만 key로 존재하고, 각 목록은 시작시간 순으로 유지한다.
  const [daySchedules, setDaySchedules] = useState({});
  // 시간 일괄 적용용 임시 입력값
  const [bulkStart, setBulkStart] = useState('09:00');
  const [bulkEnd, setBulkEnd] = useState('10:00');
  // 시간 일괄 적용 팝업 노출 여부
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  // 실제로 일괄 적용한 시간(저장 누른 값). 이후 새로 켜는 요일의 기본값으로 사용
  const [appliedBulk, setAppliedBulk] = useState(null);
  // 시간대 편집 팝오버 — index 가 null 이면 새 시간대 추가
  const [slotEditor, setSlotEditor] = useState(null);

  // 요일을 새로 켤 때 들어갈 기본 시간 — 일괄 적용한 값이 있으면 그 값, 없으면 09:00~10:00
  const defaultDayTime = appliedBulk || DEFAULT_SLOT;

  // 검증 실패 시 해당 필드로 스크롤+포커스를 보내기 위한 ref들
  const usageCountRef = useRef(null);
  const trainerRef = useRef(null);
  const exerciseRoomRef = useRef(null);
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const daysRef = useRef(null);

  // 래퍼 안의 첫 포커스 가능한 요소(input/select/button)로 스크롤 후 포커스
  const focusField = ref => {
    const node = ref?.current;
    if (!node) return;
    node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const focusable = node.querySelector('input, [role="button"], .MuiSelect-select, button');
    if (focusable) focusable.focus({ preventScroll: true });
  };

  const branchId = user?.branchId || bigUser?.branch?.branchId || userApp?.branch?.branchId;

  // 일일권(체험권)은 memberTrialTicketId만 보유하고 memberCourseTicketId가 없다.
  const isTrial = !!ticket?.memberTrialTicketId;

  const { control, watch, reset, setValue } = useForm({
    defaultValues: {
      usageCount: '',
      trainerId: '',
      exerciseRoomId: '',
      startDate: moment().toDate(),
      endDate: null,
    },
  });

  const getTrainers = async () => {
    try {
      const param = `${branchId}&courseClassType=PRIVATE_LESSON`;
      const data = await getPointTrainers(param, bigUser.token);
      setTrainers([
        { value: '', label: '담당 강사를 선택해주세요' },
        ...data.map(item => ({ value: item.trainerId, label: item.trainerName })),
      ]);
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const getRooms = async () => {
    try {
      const data = await getAllPointRooms(branchId, bigUser.token);
      // 개인레슨이라 개인레슨실과 전체사용가능 룸만 고를 수 있다.
      const selectableRooms = filterRoomsByLessonType(data || [], 'PRIVATE_LESSON');
      setRooms([
        { value: '', label: '운동실을 선택해주세요' },
        ...selectableRooms.map(item => ({ value: item.branchExerciseRoomId, label: item.branchExerciseRoomName })),
      ]);
      // 기본 강의실이 있으면 그곳을, 없으면 고를 수 있는 곳이 하나뿐일 때 자동으로 선택한다.
      // 재설정으로 돌아와 이미 복원된 값이 있으면 덮어쓰지 않는다.
      const autoRoom =
        selectableRooms.find(item => item.defaultForPrivateYn === 'Y') ||
        (selectableRooms.length === 1 ? selectableRooms[0] : null);
      if (autoRoom && !watch('exerciseRoomId')) {
        setValue('exerciseRoomId', autoRoom.branchExerciseRoomId);
      }
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  useEffect(() => {
    if (!modal.open6) return;
    getTrainers();
    getRooms();
    // 재설정으로 돌아온 경우 이전 입력값 복원
    if (modal.batchFormData) {
      const { formValues, daySchedules: savedDays, bulkStart: bs, bulkEnd: be, appliedBulk: ab } = modal.batchFormData;
      reset({
        usageCount: formValues.usageCount,
        trainerId: formValues.trainerId,
        exerciseRoomId: formValues.exerciseRoomId ?? '',
        startDate: formValues.startDate ? moment(formValues.startDate).toDate() : moment().toDate(),
        endDate: formValues.endDate ? moment(formValues.endDate).toDate() : null,
      });
      setDaySchedules(savedDays || {});
      if (bs) setBulkStart(bs);
      if (be) setBulkEnd(be);
      if (ab) setAppliedBulk(ab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal.open6]);

  // 요일 버튼 — 비어 있으면 기본 시간대 하나로 켜고, 켜져 있으면 그 요일 시간대를 모두 끈다.
  // 시간대가 여럿이면 한 번의 실수 클릭으로 다 날아가지 않게 확인을 받는다.
  const toggleDay = day => {
    const slots = daySchedules[day] || [];
    if (slots.length > 1 && !window.confirm(`${DAY_LABELS[day]}요일 시간 ${slots.length}개를 모두 지울까요?`)) {
      return;
    }
    setDaySchedules(prev => {
      const next = { ...prev };
      if (next[day]) {
        delete next[day];
      } else {
        next[day] = [{ ...defaultDayTime }];
      }
      return next;
    });
  };

  const fillDays = days => {
    setDaySchedules(prev => {
      const next = {};
      days.forEach(day => {
        next[day] = prev[day] || [{ ...defaultDayTime }];
      });
      return next;
    });
  };

  const handleResetDays = () => setDaySchedules({});

  // 시간 일괄 적용 팝업 열기 — 요일이 하나도 없으면 안내 후 막는다
  const handleOpenBulkModal = () => {
    if (Object.keys(daySchedules).length === 0) {
      toast.error('먼저 요일을 선택해주세요');
      return;
    }
    setBulkModalOpen(true);
  };

  // 팝업에서 저장 — 현재 켜진 요일 전체를 이 시간대 하나로 바꾸고 닫기
  const handleBulkApply = () => {
    if (moment(bulkEnd, 'HH:mm').isSameOrBefore(moment(bulkStart, 'HH:mm'))) {
      toast.error('종료 시간은 시작 시간보다 늦어야 합니다');
      return;
    }
    setDaySchedules(prev => {
      const next = {};
      Object.keys(prev).forEach(day => {
        next[day] = [{ startTime: bulkStart, endTime: bulkEnd }];
      });
      return next;
    });
    // 이후 새로 켜는 요일에도 같은 시간이 들어가도록 적용값 기억
    setAppliedBulk({ startTime: bulkStart, endTime: bulkEnd });
    setBulkModalOpen(false);
  };

  const openSlotEditor = (event, day, index) => {
    const slots = daySchedules[day] || [];
    const base = index === null ? suggestNextSlot(slots, defaultDayTime) : slots[index];
    setSlotEditor({ anchorEl: event.currentTarget, day, index, ...base });
  };

  // 팝오버 저장 — 시작<종료, 같은 요일의 다른 시간대와 겹치지 않아야 한다
  const handleSlotSave = () => {
    const { day, index, startTime, endTime } = slotEditor;
    if (toMinutes(endTime) <= toMinutes(startTime)) {
      toast.error('종료 시간은 시작 시간보다 늦어야 합니다');
      return;
    }
    const others = (daySchedules[day] || []).filter((_, i) => i !== index);
    const overlapped = others.find(slot => slotsOverlap(slot, { startTime, endTime }));
    if (overlapped) {
      toast.error(
        `${DAY_LABELS[day]}요일 ${overlapped.startTime}~${overlapped.endTime} 시간대와 겹칩니다`
      );
      return;
    }
    setDaySchedules(prev => ({ ...prev, [day]: sortSlots([...others, { startTime, endTime }]) }));
    setSlotEditor(null);
  };

  // 시간대 하나 삭제 — 칩의 × 와 팝오버 삭제가 같이 쓴다. 마지막 하나를 지우면 요일도 꺼진다.
  const removeSlot = (day, index) => {
    setDaySchedules(prev => {
      const next = { ...prev };
      const remaining = next[day].filter((_, i) => i !== index);
      if (remaining.length === 0) {
        delete next[day];
      } else {
        next[day] = remaining;
      }
      return next;
    });
  };

  const handleSlotDelete = () => {
    removeSlot(slotEditor.day, slotEditor.index);
    setSlotEditor(null);
  };

  // 레슨 생성(프리뷰) — 검증 후 preview API 호출하고 다음 화면으로
  const handleOpenPreview = async () => {
    const values = watch();

    // 화면 위→아래 순서로 검증하고, 비었거나 잘못된 첫 필드로 포커스를 보낸다
    const usageMax = Number(values.usageCount) || 0;
    if (usageMax < 1) {
      toast.error('사용 횟수를 선택해주세요');
      focusField(usageCountRef);
      return;
    }
    if (!values.trainerId) {
      toast.error('담당 강사를 선택해주세요');
      focusField(trainerRef);
      return;
    }
    if (!values.exerciseRoomId) {
      toast.error('운동실을 선택해주세요');
      focusField(exerciseRoomRef);
      return;
    }
    if (!values.startDate) {
      toast.error('시작 날짜를 선택해주세요');
      focusField(startDateRef);
      return;
    }
    if (!values.endDate) {
      toast.error('종료 날짜를 선택해주세요');
      focusField(endDateRef);
      return;
    }
    if (!validateDateRange(values.startDate, values.endDate)) {
      toast.error('기간을 다시 확인해주세요');
      focusField(startDateRef);
      return;
    }

    // 시간대는 팝오버 저장 시 시작<종료·같은 요일 겹침을 이미 검사했다
    const classTimeInfoList = PRIVATE_DAY_ORDER.flatMap(day =>
      (daySchedules[day] || []).map(slot => ({ dayOfWeek: day, startTime: slot.startTime, endTime: slot.endTime }))
    );
    if (classTimeInfoList.length === 0) {
      toast.error('예약 요일과 시간을 선택해주세요');
      focusField(daysRef);
      return;
    }

    const payload = {
      // 일일권이면 memberTrialTicketId만, 수강권이면 memberCourseTicketId만 보낸다(둘 다 보내지 않음 → 406 방지)
      ...(isTrial
        ? { memberTrialTicketId: ticket?.memberTrialTicketId }
        : { memberCourseTicketId: ticket?.memberCourseTicketId }),
      // 공유받은 수강권(#154)이면 이 회원으로 예약한다(없으면 서버가 수강권 소유자로 본다).
      memberId: user?.memberId,
      trainerId: values.trainerId,
      exerciseRoomId: values.exerciseRoomId,
      branchId,
      registrationStartDate: moment(values.startDate).format('YYYY-MM-DD'),
      registrationEndDate: moment(values.endDate).format('YYYY-MM-DD'),
      classTimeInfoList,
    };

    try {
      const result = await getPrivateBatchPreview(payload, bigUser.token);
      setModal({
        ...modal,
        open6: false,
        openFindLesson: true,
        batchReservationData: {
          ...result,
          isPrivateBatch: true,
          previewPayload: payload,
          usageMax,
          memberCourseTicketId: isTrial ? undefined : ticket?.memberCourseTicketId,
          memberTrialTicketId: isTrial ? ticket?.memberTrialTicketId : undefined,
          remainingCount: result?.remainingCount ?? ticket?.remainingCount ?? 0,
        },
        batchFormData: {
          formValues: {
            usageCount: values.usageCount,
            trainerId: values.trainerId,
            exerciseRoomId: values.exerciseRoomId,
            startDate: moment(values.startDate).format('YYYY-MM-DD'),
            endDate: moment(values.endDate).format('YYYY-MM-DD'),
          },
          daySchedules: { ...daySchedules },
          bulkStart,
          bulkEnd,
          appliedBulk,
        },
      });
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  // 사용 횟수 옵션 (1 ~ 잔여 이용횟수)
  const usageCountOptions = [
    { value: '', label: '선택' },
    ...Array.from({ length: Math.max(0, ticket?.remainingCount || 0) }, (_, i) => ({
      value: i + 1,
      label: `${i + 1}회`,
    })),
  ];

  // 하단 요약 — 주당 회차와 기간 안에 만들어질 후보 회차 수(강사/회원 충돌 여부는 다음 화면에서 확인)
  const weeklyCount = Object.values(daySchedules).reduce((sum, slots) => sum + slots.length, 0);
  const startDate = watch('startDate');
  const endDate = watch('endDate');
  const usageCount = Number(watch('usageCount')) || 0;
  let periodCount = null;
  if (weeklyCount > 0 && startDate && endDate && !moment(endDate).isBefore(moment(startDate), 'day')) {
    periodCount = 0;
    for (const date = moment(startDate).startOf('day'); !date.isAfter(moment(endDate), 'day'); date.add(1, 'day')) {
      periodCount += (daySchedules[PRIVATE_DAY_ORDER[date.isoWeekday() - 1]] || []).length;
    }
  }

  const actionBtnSx = {
    height: '30px',
    padding: '4px 12px',
    fontSize: '12.5px',
    fontWeight: '600',
    textTransform: 'none',
    borderRadius: '8px',
    backgroundColor: '#f0f0f0',
    color: '#333333',
    border: 'none',
    boxShadow: 'none',
    '&:hover': { backgroundColor: '#e2e2e2', boxShadow: 'none' },
  };

  const dropdownSx = { width: '260px', '& .MuiInputBase-root': { borderRadius: '4px', height: '41px' } };

  return (
    <Box sx={{ ...modalStyle, width: '680px' }} ref={ref}>
      {/* 헤더 */}
      <Typography
        component="div"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: '14.5px 24px',
          height: '48px',
          backgroundColor: '#262626',
          borderRadius: '16px 16px 0 0',
        }}>
        <Box sx={{ fontSize: '16px', fontWeight: '600', color: '#ffffff' }}>개인레슨 일괄생성</Box>
        <Button
          onClick={onClose}
          sx={{
            color: '#ffffff',
            width: '40px',
            height: '40px',
            padding: '0px',
            border: '1px solid #262626',
            backgroundColor: '#262626',
            minWidth: 'unset',
          }}>
          <CloseIcon sx={{ width: '20px', height: '20px' }} />
        </Button>
      </Typography>

      {/* 컨텐츠 */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: '24px 32px' }}>
        {/* 수강권 요약 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            p: '14px 18px',
            backgroundColor: '#f5f7f8',
            borderRadius: '10px',
          }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '12px', color: '#8a8a8a', mb: '2px' }}>
              {isTrial ? '일일권' : '수강권'}
            </Typography>
            <Typography
              sx={{
                fontSize: '15px',
                fontWeight: '700',
                color: '#262626',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
              {isTrial ? ticket?.trialName : ticket?.ticketName}
            </Typography>
            <Typography sx={{ fontSize: '12.5px', color: '#666666', mt: '2px' }}>
              유효기간 {ticket?.issueDate} ~ {ticket?.expireDate}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            <Typography sx={{ fontSize: '12px', color: '#8a8a8a', mb: '2px' }}>잔여</Typography>
            <Typography sx={{ fontSize: '20px', fontWeight: '700', color: '#0F7F85' }}>
              {ticket?.remainingCount}
              <Box component="span" sx={{ fontSize: '14px', ml: '2px' }}>
                회
              </Box>
            </Typography>
          </Box>
        </Box>

        {/* 수업 정보 */}
        <Box sx={sectionSx}>
          <Typography sx={sectionTitleSx}>수업 정보</Typography>
          <Box ref={usageCountRef} sx={fieldRowSx}>
            <Typography sx={fieldLabelSx}>사용 횟수</Typography>
            <FormInputDropdown control={control} sx={dropdownSx} name="usageCount" options={usageCountOptions} />
          </Box>
          <Box ref={trainerRef} sx={fieldRowSx}>
            <Typography sx={fieldLabelSx}>담당 강사</Typography>
            <FormInputDropdown control={control} sx={dropdownSx} name="trainerId" options={trainers} />
          </Box>
          <Box ref={exerciseRoomRef} sx={{ ...fieldRowSx, mb: 0 }}>
            <Typography sx={fieldLabelSx}>운동실</Typography>
            <FormInputDropdown control={control} sx={dropdownSx} name="exerciseRoomId" options={rooms} />
          </Box>
        </Box>

        {/* 예약 기간 */}
        <Box sx={sectionSx}>
          <Typography sx={sectionTitleSx}>예약 기간</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Box ref={startDateRef} sx={{ display: 'flex' }}>
              <FormInputDate
                name="startDate"
                control={control}
                placeholder={t('DD/MM/YYYY')}
                components={{ OpenPickerIcon: CalendarIcon }}
                renderInput={dateFieldRender(t)}
              />
            </Box>
            <Typography sx={{ color: '#333333', fontSize: '14px', fontWeight: '600' }}>~</Typography>
            <Box ref={endDateRef} sx={{ display: 'flex' }}>
              <FormInputDate
                name="endDate"
                control={control}
                placeholder={t('DD/MM/YYYY')}
                minDate={startDate || null}
                disabled={!startDate}
                components={{ OpenPickerIcon: CalendarIcon }}
                renderInput={dateFieldRender(t)}
              />
            </Box>
          </Box>
        </Box>

        {/* 요일·시간 — 요일마다 한 줄, 줄마다 시간대 칩을 여러 개 */}
        <Box sx={sectionSx} ref={daysRef}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '12px' }}>
            <Typography sx={{ ...sectionTitleSx, mb: 0 }}>요일·시간</Typography>
            <Box sx={{ display: 'flex', gap: '6px' }}>
              <Button variant="contained" onClick={() => fillDays(PRIVATE_DAY_ORDER.slice(0, 5))} sx={actionBtnSx}>
                월-금 선택
              </Button>
              <Button variant="contained" onClick={() => fillDays(PRIVATE_DAY_ORDER)} sx={actionBtnSx}>
                전체 선택
              </Button>
              <Button variant="contained" onClick={handleOpenBulkModal} sx={actionBtnSx}>
                시간 일괄 적용
              </Button>
              <Button variant="contained" onClick={handleResetDays} sx={actionBtnSx}>
                초기화
              </Button>
            </Box>
          </Box>
          <Typography sx={{ fontSize: '12.5px', color: '#8a8a8a', mb: '10px' }}>
            요일을 누르면 켜고 끌 수 있고, 시간을 누르면 수정, ×를 누르면 삭제됩니다. 한 요일에 여러 시간을 추가할 수 있습니다.
          </Typography>

          <Box sx={{ border: '1px solid #e6e6e6', borderRadius: '10px', overflow: 'hidden' }}>
            {PRIVATE_DAY_ORDER.map((day, rowIndex) => {
              const slots = daySchedules[day] || [];
              const on = slots.length > 0;
              const isWeekend = day === 'SATURDAY' || day === 'SUNDAY';
              return (
                <Box
                  key={day}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    minHeight: '52px',
                    px: '12px',
                    py: '8px',
                    backgroundColor: on ? '#f2fafa' : '#ffffff',
                    borderTop: rowIndex === 0 ? 'none' : '1px solid #efefef',
                  }}>
                  <Button
                    onClick={() => toggleDay(day)}
                    sx={{
                      width: '36px',
                      minWidth: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      fontSize: '14px',
                      fontWeight: '700',
                      flexShrink: 0,
                      backgroundColor: on ? '#0F7F85' : '#f0f0f0',
                      color: on ? '#ffffff' : isWeekend ? '#c0392b' : '#555555',
                      '&:hover': { backgroundColor: on ? '#0d6b70' : '#e2e2e2' },
                    }}>
                    {DAY_LABELS[day]}
                  </Button>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', flex: 1 }}>
                    {slots.map((slot, index) => (
                      // 시간 글자를 누르면 수정 팝오버, 오른쪽 × 는 바로 삭제
                      <Box
                        key={`${slot.startTime}-${slot.endTime}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          height: '32px',
                          borderRadius: '16px',
                          backgroundColor: '#ffffff',
                          border: '1px solid #0F7F85',
                          overflow: 'hidden',
                        }}>
                        <Button
                          onClick={event => openSlotEditor(event, day, index)}
                          sx={{
                            height: '100%',
                            minWidth: 0,
                            pl: '12px',
                            pr: '2px',
                            borderRadius: 0,
                            fontSize: '13px',
                            fontWeight: '600',
                            textTransform: 'none',
                            color: '#0F7F85',
                            '&:hover': { backgroundColor: '#e3f3f3' },
                          }}>
                          {slot.startTime} ~ {slot.endTime}
                        </Button>
                        <IconButton
                          aria-label={`${DAY_LABELS[day]}요일 ${slot.startTime}~${slot.endTime} 삭제`}
                          onClick={() => removeSlot(day, index)}
                          sx={{
                            width: '26px',
                            height: '100%',
                            pl: 0,
                            pr: '6px',
                            borderRadius: 0,
                            color: '#7aa9ab',
                            '&:hover': { color: '#c0392b', backgroundColor: '#fdf0ee' },
                          }}>
                          <CloseIcon sx={{ width: '15px', height: '15px' }} />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      onClick={event => openSlotEditor(event, day, null)}
                      sx={{
                        height: '32px',
                        px: '10px',
                        borderRadius: '16px',
                        fontSize: '13px',
                        fontWeight: '600',
                        textTransform: 'none',
                        color: '#8a8a8a',
                        border: '1px dashed #c4c4c4',
                        '&:hover': { borderColor: '#0F7F85', color: '#0F7F85', backgroundColor: 'transparent' },
                      }}>
                      + 시간 추가
                    </Button>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* 요약 */}
        <Box
          sx={{
            mt: '16px',
            p: '12px 16px',
            borderRadius: '10px',
            backgroundColor: weeklyCount > 0 ? '#e9f5f5' : '#f5f5f5',
            fontSize: '13.5px',
            color: '#333333',
          }}>
          {weeklyCount === 0 ? (
            '요일과 시간을 선택해주세요'
          ) : (
            <>
              주 <b>{weeklyCount}회</b>
              {periodCount !== null && (
                <>
                  {' · 기간 내 총 '}
                  <b style={{ color: '#0F7F85' }}>{periodCount}회</b> 생성 후보
                </>
              )}
              {usageCount > 0 && ` · 사용 횟수 ${usageCount}회`}
            </>
          )}
        </Box>

        {/* 하단 버튼 */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', mt: '24px' }}>
          <Button
            variant="contained"
            onClick={onClose}
            sx={{
              backgroundColor: '#666666',
              color: '#ffffff',
              fontSize: '16px',
              fontWeight: '600',
              padding: '12px 32px',
              borderRadius: '12px',
              height: '40px',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#555555', boxShadow: 'none' },
            }}>
            취소
          </Button>
          <Button
            variant="contained"
            onClick={handleOpenPreview}
            sx={{
              backgroundColor: '#0F7F85',
              color: '#ffffff',
              fontSize: '16px',
              fontWeight: '600',
              padding: '12px 24px',
              borderRadius: '12px',
              width: '200px',
              height: '40px',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#0d6b70', boxShadow: 'none' },
            }}>
            레슨 생성
          </Button>
        </Box>
      </Box>

      {/* 시간대 편집 팝오버 — 추가/수정/삭제 */}
      <Popover
        open={!!slotEditor}
        anchorEl={slotEditor?.anchorEl}
        onClose={() => setSlotEditor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
        {slotEditor && (
          <Box sx={{ p: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <Typography sx={{ fontSize: '14px', fontWeight: '700', color: '#262626' }}>
              {DAY_LABELS[slotEditor.day]}요일 {slotEditor.index === null ? '시간 추가' : '시간 수정'}
            </Typography>
            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <TimePicker
                value={slotEditor.startTime}
                onChange={v => setSlotEditor(prev => ({ ...prev, startTime: v }))}
                fallback="09:00"
              />
              <Typography sx={{ color: '#333', fontSize: '14px' }}>~</Typography>
              <TimePicker
                value={slotEditor.endTime}
                onChange={v => setSlotEditor(prev => ({ ...prev, endTime: v }))}
                fallback="10:00"
              />
            </Box>
            <Box sx={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              {slotEditor.index !== null && (
                <Button
                  onClick={handleSlotDelete}
                  sx={{ ...actionBtnSx, mr: 'auto', color: '#c0392b', backgroundColor: '#fdf0ee', '&:hover': { backgroundColor: '#f9dcd8' } }}>
                  삭제
                </Button>
              )}
              <Button onClick={() => setSlotEditor(null)} sx={actionBtnSx}>
                취소
              </Button>
              <Button
                onClick={handleSlotSave}
                sx={{ ...actionBtnSx, color: '#ffffff', backgroundColor: '#0F7F85', '&:hover': { backgroundColor: '#0d6b70' } }}>
                {slotEditor.index === null ? '추가' : '저장'}
              </Button>
            </Box>
          </Box>
        )}
      </Popover>

      {/* 시간 일괄 적용 팝업 — 저장하면 선택된 모든 요일이 이 시간대 하나로 바뀐다 */}
      <Modal open={bulkModalOpen} onClose={() => setBulkModalOpen(false)}>
        <Box sx={bulkModalStyle}>
          <Typography sx={{ fontSize: '16px', fontWeight: '600', color: '#262626' }}>시간 일괄 적용</Typography>
          <Typography sx={{ fontSize: '13px', color: '#666666', mt: '-8px' }}>
            선택된 요일의 시간이 모두 이 시간 하나로 바뀝니다.
          </Typography>
          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center' }}>
            <TimePicker value={bulkStart} onChange={setBulkStart} fallback="09:00" />
            <Typography sx={{ color: '#333', fontSize: '14px' }}>~</Typography>
            <TimePicker value={bulkEnd} onChange={setBulkEnd} fallback="10:00" />
          </Box>
          <Box sx={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={() => setBulkModalOpen(false)}
              sx={{
                backgroundColor: '#666666',
                color: '#ffffff',
                fontSize: '14px',
                fontWeight: '600',
                padding: '8px 24px',
                borderRadius: '8px',
                boxShadow: 'none',
                '&:hover': { backgroundColor: '#555555', boxShadow: 'none' },
              }}>
              취소
            </Button>
            <Button
              variant="contained"
              onClick={handleBulkApply}
              sx={{
                backgroundColor: '#0F7F85',
                color: '#ffffff',
                fontSize: '14px',
                fontWeight: '600',
                padding: '8px 24px',
                borderRadius: '8px',
                boxShadow: 'none',
                '&:hover': { backgroundColor: '#0d6b70', boxShadow: 'none' },
              }}>
              저장
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
});

// ---------------------------------------------------------------------------
// 그룹레슨 분기 — 기존 "기존 수업 검색→예약" 흐름 그대로 유지(동작/마크업 변경 금지).
// ---------------------------------------------------------------------------
const GroupLessonBatchForm = forwardRef(({ modal, setModal, bigUser, ticket, user, onClose }, ref) => {
  const { t } = useTranslation();
  const toast = useToast();
  const userApp = useSelector(state => state.user.user);
  const [selectedDays, setSelectedDays] = useState({
    SUNDAY: false,
    MONDAY: false,
    TUESDAY: false,
    WEDNESDAY: false,
    THURSDAY: false,
    FRIDAY: false,
    SATURDAY: false,
  });
  const [branches, setBranches] = useState([]);

  const { control, watch, reset } = useForm({
    defaultValues: {
      startDate: null,
      endDate: null,
      startTime: '09:00',
      endTime: '10:00',
      trainerId: '',
      memo: '',
      reservationCount: '',
      usageCount: '',
      centerId: '',
    },
  });

  // 일일권(체험권)은 memberTrialTicketId만 보유하고 memberCourseTicketId가 없다.
  const isTrial = !!ticket?.memberTrialTicketId;

  const handleOpenFindLesson = async () => {
    const values = watch();

    if (!values.startDate) {
      toast.error('시작 날짜를 선택해주세요');
      return;
    }
    if (!values.endDate) {
      toast.error('종료 날짜를 선택해주세요');
      return;
    }
    if (!validateDateRange(values.startDate, values.endDate)) {
      toast.error('기간을 다시 확인해주세요');
      return;
    }

    const selectedDayList = Object.keys(selectedDays).filter(day => selectedDays[day]);

    if (!values.startTime || !values.endTime) {
      toast.error('시작 시간과 종료 시간을 입력해주세요');
      return;
    }

    const startTimeMoment = moment(values.startTime, 'HH:mm');
    const endTimeMoment = moment(values.endTime, 'HH:mm');

    if (endTimeMoment.isSameOrBefore(startTimeMoment)) {
      toast.error('종료 시간은 시작 시간보다 늦어야 합니다');
      return;
    }

    const count = Number(values.reservationCount) || 0;
    if (count < 1) {
      toast.error('한 개 이상의 예약횟수를 설정해주세요');
      return;
    }

    try {
      const params = {
        branchId: user?.branchId || bigUser?.branch?.branchId || userApp?.branch?.branchId,
        startDate: moment(values.startDate).format('YYYY-MM-DD'),
        endDate: moment(values.endDate).format('YYYY-MM-DD'),
        startTime: formatTime(values.startTime),
        endTime: formatTime(values.endTime),
        courseClassType: ticket?.classType || 'GROUP_LESSON',
        reservationCount: Number(values.reservationCount),
        ...(isTrial
          ? { memberTrialTicketId: ticket?.memberTrialTicketId }
          : { memberCourseTicketId: ticket?.memberCourseTicketId }),
        // 공유받은 수강권(#154)이면 이 회원 기준으로 조회한다.
        memberId: user?.memberId,
      };

      selectedDayList.forEach(day => {
        params[`dayOfWeeks`] = params[`dayOfWeeks`] ? `${params[`dayOfWeeks`]},${day}` : day;
      });

      const result = await searchBatchReservationLessons(params, bigUser.token);

      setModal({
        ...modal,
        open6: false,
        openFindLesson: true,
        batchReservationData: {
          ...result,
          searchParams: params,
          memo: values.memo,
          memberId: user.memberId,
          tickets: [ticket?.courseTicketId],
          memberCourseTicketId: isTrial ? undefined : ticket?.memberCourseTicketId,
          memberTrialTicketId: isTrial ? ticket?.memberTrialTicketId : undefined,
          remainingCount: ticket?.remainingCount || 0,
          reservationCount: Number(values.reservationCount),
        },
        batchFormData: {
          formValues: values,
          selectedDays: { ...selectedDays },
        },
      });
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const getBranches = async () => {
    try {
      let data;
      if (bigUser.userRole === 'ROLE_SUPERADMIN') {
        data = await getAllBranchOption(bigUser.token, bigUser.branch?.branchId);
      } else {
        data = await getHeadBranchId(bigUser.token, bigUser.headBranch?.headBranchId);
      }

      const branchArray = [
        { value: '', label: '센터를 선택해주세요' },
        ...(data || []).map(item => ({
          value: item.branchId,
          label: item.branchName,
        })),
      ];
      setBranches(branchArray);
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const handleDayChange = day => {
    setSelectedDays(prev => ({ ...prev, [day]: !prev[day] }));
  };

  const formatTime = time => {
    if (!time) return '09:00';
    if (time.includes(':')) {
      const parts = time.split(':');
      return `${parts[0]}:${parts[1]}`;
    }
    return time;
  };

  useEffect(() => {
    if (modal.open6) {
      getBranches();

      if (modal.batchFormData) {
        const { formValues, selectedDays: savedDays } = modal.batchFormData;
        reset(formValues);
        setSelectedDays(savedDays);
      } else {
        const ticketBranchId = ticket?.branchId || user?.branchId || bigUser?.branch?.branchId;
        if (ticketBranchId) {
          reset({
            startDate: null,
            endDate: null,
            startTime: '09:00',
            endTime: '10:00',
            trainerId: '',
            memo: '',
            reservationCount: '',
            usageCount: '',
            centerId: ticketBranchId,
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal.open6]);

  return (
    <Box sx={modalStyle} ref={ref}>
      {/* 헤더 영역 */}
      <Typography
        id="lesson-batch-modal-title"
        component="div"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: '14.5px 24px',
          height: '48px',
          backgroundColor: '#262626',
          borderRadius: '16px 16px 0 0',
        }}>
        <Box sx={{ fontSize: '16px', fontWeight: '600', color: '#ffffff' }}>그룹레슨 일괄예약</Box>
        <Button
          onClick={onClose}
          sx={{
            color: '#ffffff',
            width: '40px',
            height: '40px',
            padding: '0px',
            border: '1px solid #262626',
            backgroundColor: '#262626',
            minWidth: 'unset',
          }}>
          <CloseIcon sx={{ width: '20px', height: '20px' }} />
        </Button>
      </Typography>

      {/* 컨텐츠 영역 */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: '32px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {/* 수강권 정보 */}
          <Box sx={{ display: 'flex', flexDirection: 'row', mb: '16px', alignItems: 'center', height: '44px' }}>
            <Typography sx={labelSx}>{isTrial ? '일일권명' : '수강권명'}</Typography>
            <Typography sx={{ fontSize: '16px', fontWeight: '600', color: '#262626', ml: '24px' }}>
              {isTrial ? ticket?.trialName : ticket?.ticketName}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'row', mb: '16px', alignItems: 'center', height: '44px' }}>
            <Typography sx={labelSx}>잔여 이용횟수</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', ml: '24px' }}>
              <Typography sx={{ fontSize: '16px', fontWeight: '600', color: '#262626' }}>
                {ticket?.remainingCount}
              </Typography>
              <Typography sx={{ fontSize: '16px', fontWeight: '600', color: '#262626', ml: '4px' }}>회</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'row', mb: '16px', alignItems: 'center', height: '44px' }}>
            <Typography sx={labelSx}>유효기간</Typography>
            <Typography sx={{ fontSize: '16px', fontWeight: '600', color: '#262626', ml: '24px' }}>
              {ticket?.issueDate} ~ {ticket?.expireDate}
            </Typography>
          </Box>

          {/* 기간 */}
          <Box sx={{ display: 'flex', flexDirection: 'row', mb: '16px', alignItems: 'center', height: '44px' }}>
            <Typography sx={labelSx}>예약기간</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', ml: '24px' }}>
              <FormInputDate
                name="startDate"
                control={control}
                placeholder={t('DD/MM/YYYY')}
                components={{ OpenPickerIcon: CalendarIcon }}
                renderInput={dateFieldRender(t)}
              />
              <Typography sx={{ color: '#333333', fontSize: '14px', fontWeight: '600' }}>-</Typography>
              <FormInputDate
                name="endDate"
                control={control}
                placeholder={t('DD/MM/YYYY')}
                minDate={watch('startDate') || null}
                disabled={!watch('startDate')}
                components={{ OpenPickerIcon: CalendarIcon }}
                renderInput={dateFieldRender(t)}
              />
            </Box>
          </Box>
          {/* 요일 선택 */}
          <Box sx={{ display: 'flex', flexDirection: 'row', mb: '16px', alignItems: 'center', height: '41px' }}>
            <Typography sx={labelSx}>예약요일</Typography>
            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', ml: '24px' }}>
              {DAY_KEYS.map(day => (
                <Button
                  key={day}
                  onClick={() => handleDayChange(day)}
                  variant="contained"
                  sx={{
                    width: '56px',
                    minWidth: '56px',
                    height: '41px',
                    padding: '12px 16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    textTransform: 'none',
                    borderRadius: '12px',
                    backgroundColor: selectedDays[day] ? '#0F7F85' : '#dcdcdc',
                    color: selectedDays[day] ? '#fff' : '#333333',
                    border: 'none',
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: selectedDays[day] ? '#0d6b70' : '#c8c8c8',
                      boxShadow: 'none',
                    },
                  }}>
                  {DAY_LABELS[day]}
                </Button>
              ))}
            </Box>
          </Box>

          {/* 시간 선택 */}
          <Box sx={{ display: 'flex', flexDirection: 'row', mb: '16px', alignItems: 'center', height: '41px' }}>
            <Typography sx={labelSx}>예약시간</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', ml: '24px' }}>
              <Controller
                name="startTime"
                control={control}
                render={({ field }) => (
                  <TimePicker value={field.value} onChange={field.onChange} fallback="09:00" />
                )}
              />
              <Typography sx={{ color: '#333', fontSize: '14px' }}>~</Typography>
              <Controller
                name="endTime"
                control={control}
                render={({ field }) => (
                  <TimePicker value={field.value} onChange={field.onChange} fallback="10:00" />
                )}
              />
            </Box>
          </Box>

          {/* 그룹레슨: 예약횟수 */}
          <Box sx={{ display: 'flex', flexDirection: 'row', mb: '16px', alignItems: 'center', height: '41px' }}>
            <Typography sx={labelSx}>예약횟수</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', ml: '24px' }}>
              <Controller
                name="reservationCount"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    size="small"
                    placeholder="0"
                    inputProps={{ min: 0 }}
                    onChange={e => {
                      const value = e.target.value;
                      if (value === '' || Number(value) >= 0) {
                        field.onChange(value);
                      }
                    }}
                    sx={{
                      width: '80px',
                      '& .MuiInputBase-root': {
                        borderRadius: '4px',
                        height: '41px',
                        border: '1px solid #b3b3b3',
                        '& input': {
                          fontSize: '14px',
                          fontWeight: '600',
                          textAlign: 'center',
                          color: '#333333',
                        },
                      },
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    }}
                  />
                )}
              />
              <Typography sx={{ color: '#333333', fontSize: '14px', fontWeight: '600' }}>회</Typography>
            </Box>
          </Box>

          {/* 그룹레슨: 센터 */}
          <Box sx={{ display: 'flex', flexDirection: 'row', mb: '16px', alignItems: 'center', height: '41px' }}>
            <Typography sx={labelSx}>센터</Typography>
            <FormInputDropdown
              control={control}
              disabled={!!watch('centerId')}
              sx={{
                width: '240px',
                ml: '24px',
                '& .MuiInputBase-root': {
                  borderRadius: '4px',
                  height: '41px',
                  backgroundColor: watch('centerId') ? '#f5f5f5' : 'transparent',
                },
                '& .MuiSelect-select': {
                  fontSize: '14px',
                  fontWeight: '600',
                  color: watch('centerId') ? '#333333' : '#b3b3b3',
                },
              }}
              name="centerId"
              options={branches}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', mt: '32px' }}>
          <Button
            variant="contained"
            onClick={onClose}
            sx={{
              backgroundColor: '#666666',
              color: '#ffffff',
              fontSize: '16px',
              fontWeight: '600',
              padding: '12px 32px',
              borderRadius: '12px',
              height: '40px',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#555555', boxShadow: 'none' },
            }}>
            취소
          </Button>
          <Button
            variant="contained"
            onClick={handleOpenFindLesson}
            sx={{
              backgroundColor: '#0F7F85',
              color: '#ffffff',
              fontSize: '16px',
              fontWeight: '600',
              padding: '12px 24px',
              borderRadius: '12px',
              width: '200px',
              height: '40px',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#0d6b70', boxShadow: 'none' },
            }}>
            레슨찾기
          </Button>
        </Box>
      </Box>
    </Box>
  );
});

PrivateLessonBatchForm.displayName = 'PrivateLessonBatchForm';
GroupLessonBatchForm.displayName = 'GroupLessonBatchForm';

const ChangeLessonBatchModal = ({ modal, setModal, ticket, ...rest }) => {
  const isGroupLesson = ticket?.classType === 'GROUP_LESSON';

  const onClose = () => {
    setModal({ ...modal, open6: false, batchFormData: null, batchReservationData: null });
  };

  return (
    <Modal
      open={modal.open6}
      onClose={onClose}
      disableEscapeKeyDown
      aria-labelledby="lesson-batch-modal-title"
      aria-describedby="lesson-batch-modal-description">
      {isGroupLesson ? (
        <GroupLessonBatchForm modal={modal} setModal={setModal} ticket={ticket} onClose={onClose} {...rest} />
      ) : (
        <PrivateLessonBatchForm modal={modal} setModal={setModal} ticket={ticket} onClose={onClose} {...rest} />
      )}
    </Modal>
  );
};

export default ChangeLessonBatchModal;
